import type { Account, AccountDTO, AccountsDTO } from "../interfaces/accounts.interface";
import type { UserContact, UserContactDTO, UserContactsDTO } from "../interfaces/user-info.interface";
import type { AnnotationsDTO, AnnotationDTO, Annotation } from "../interfaces/annotation.interface";
import type { BusinessArea, BusinessAreaDTO, BusinessAreasDTO } from "../interfaces/business-area-ce.interface";
import type { Case, CaseDTO, CasesDTO } from "../interfaces/case.interface";
import type { CategoryDTO, CategoriesDTO, Category } from "../interfaces/category.interface";
import type { MessageCenter, MessageCenterDTO, MessagesCenterDTO } from "../interfaces/messageCenter.interface";
import type { CourseDTO, CourseAttenderDTO, CourseAttendersDTO, CoursesDTO, Course } from "../interfaces/course.interface";
import type {
    KnowledgeArticleDTO,
    KnowledgeArticlesDTO,
    FeedbackKnowledgeArticle,
    AttachmentDTO,
    AttachmentsDTO,
    ArticleContentAttachmentDTO,
    KnowledgeArticle,
    Attachment,
    ArticleContentAttachment
} from "../interfaces/knowledgearticle.interface";

export function mapUserContacts(response: UserContactsDTO): UserContact[] {
    return response.value.map(
        (contact: UserContactDTO): UserContact => ({
            contactId: contact.contactid,
            emailAddress1: contact.emailaddress1,
            emailAddress2: contact.emailaddress2,
            emailAddress3: contact.emailaddress3,
            fullname: contact.fullname,
            firstname: contact.firstname,
            lastname: contact.lastname,
            title: contact.svk_title,
            contactgroups: contact.svk_contactgroup,
            mobilephone: contact.mobilephone,
            landlinePhone: contact.telephone3,
            preferredContactMethod: contact.preferredcontactmethodcode,
            svk_lastmodifiedonline: contact.svk_lastmodifiedonline,
            statecode: contact.statecode,
            statuscode: contact.statuscode,
            hasWebAccess: contact.svk_webaccess,
            svk_smsnotificationnumber: contact.svk_smsnotificationnumber,
            ["svk_LastModifiedOnlineBy@odata.bind"]: contact["svk_LastModifiedOnlineBy@odata.bind"],
            currentAccount: {
                accountId: contact.parentcustomerid_account.accountid,
                accountNumber: contact.parentcustomerid_account.accountnumber,
                name: contact.parentcustomerid_account.name
            }
        })
    );
}

export function mapAccount(response: AccountDTO): Account {
    return {
        accountId: response.accountid,
        name: response.name,
        emailAddress1: response.emailaddress1,
        composite: response.address1_composite,
        line: response.address1_line1,
        city: response.address1_city,
        postalcode: response.address1_postalcode,
        country: response.address1_country,
        hasAdminRights: response.svk_hasaccountadmin ?? false
    };
}

export function mapAccounts(response: AccountsDTO): Account[] {
    return response.value.map(
        (account: AccountDTO): Account => ({
            accountId: account.accountid,
            name: account.name,
            emailAddress1: account.emailaddress1,
            composite: account.address1_composite,
            line: account.address1_line1,
            city: account.address1_city,
            postalcode: account.address1_postalcode,
            country: account.address1_country,
            hasAdminRights: account.svk_hasaccountadmin ?? false
        })
    );
}

export function mapAnnotations(response: AnnotationsDTO): Annotation[] {
    return response.value.map(
        (annotation: AnnotationDTO): Annotation => ({
            objectId: annotation._objectid_value,
            filename: annotation.filename,
            documentBody: annotation.documentbody,
            mediatype: annotation.mimetype,
            annotationid: annotation.annotationid
        })
    );
}

export function mapBusinessAreas(response: BusinessAreasDTO): BusinessArea[] {
    return response.value.map(
        (area: BusinessAreaDTO): BusinessArea => ({
            id: area.svk_businessareaid,
            name: area.svk_name,
            count: area.count
        })
    );
}

export function mapBusinessArea(response: BusinessAreaDTO): BusinessArea {
    return {
        id: response.svk_businessareaid,
        name: response.svk_name,
        count: response.count
    };
}

export function mapCases(response: CasesDTO): Case[] {
    return response.value.map(
        (caseItem: CaseDTO): Case => ({
            title: caseItem.title,
            ticketnumber: caseItem.ticketnumber,
            createdon: caseItem.createdon,
            incidentid: caseItem.incidentid,
            description: caseItem.description,
            solution: caseItem.svk_solution,
            statuscode: caseItem.statuscode,
            statecode: caseItem.statecode,
            businessareaId: caseItem._svk_businessarea_value,
            accountId: caseItem._svk_account_value,
            reportedbyId: caseItem._svk_reportedby_value,
            caseorigin: caseItem.caseorigincode,
            casecategoryId: caseItem._svk_casecategory_value,
            dueDate: caseItem.svk_duedate,
            showOnWeb: caseItem.svk_showonweb
        })
    );
}

export function mapCategories(response: CategoriesDTO): Category[] {
    return response.value.map(
        (category: CategoryDTO): Category => ({
            categoryId: category.svk_casecategoryid,
            name: category.svk_name
        })
    );
}

export function mapCategory(response: CategoryDTO): Category {
    return {
        categoryId: response.svk_casecategoryid,
        name: response.svk_name
    };
}

export function mapMessageCenters(response: MessagesCenterDTO): MessageCenter[] {
    return response.value.map(
        (message: MessageCenterDTO): MessageCenter => ({
            createdon: message.createdon,
            activeFrom: message.svk_activefrom,
            activeTo: message.svk_activeto,
            id: message.svk_messagecenterid ?? "",
            message: message.svk_message,
            title: message.svk_name,
            priority: message.svk_priority,
            showOnCase: message.svk_showasnewcasemessage,
            businessArea: message._svk_businessarea_value
        })
    );
}

export function mapMessageCenter(response: MessageCenterDTO): MessageCenter {
    return {
        createdon: response.createdon,
        activeFrom: response.svk_activefrom,
        activeTo: response.svk_activeto,
        id: response.svk_messagecenterid ?? "",
        message: response.svk_message,
        title: response.svk_name
    };
}

export function mapCourses(response: CoursesDTO): Course[] {
    return response.value.map(
        (course: CourseDTO): Course => ({
            courseId: course.svk_coursesid,
            businessAreaId: course._svk_businessarea_value,
            name: course.svk_name,
            createdon: course.createdon,
            statecode: course.statecode,
            statuscode: course.statuscode,
            registrationWindowStart: course.svk_registrationwindowstart,
            registrationWindowEnd: course.svk_registrationwindowend,
            overriddenCreatedon: course.overriddencreatedon,
            publishedWeb: course.svk_publishonweb,
            maxParticipants: course.svk_maximumnumberofparticipants,
            minimumParticipants: course.svk_minimumnnumberofparticipants,
            // courseDuration: course.,
            coursePrice: course.svk_courseprice,
            courseDate: course.svk_timefrom,
            decription: course.svk_decription,
            seatsOccupied: course.svk_numberofseatstaken,
            seatsLeft: course.svk_numberofseatsleft,
            courseAddress: course.svk_placeofcourse,
            currency: course._transactioncurrencyid_value,
            courseTo: course.svk_timeto,
            courseFrom: course.svk_timefrom,
            courseInstructor: course.svk_courseinstructor
        })
    );
}

export function mapCourseAttenders(response: CourseAttendersDTO): CourseAttendersDTO {
    return {
        ["@odata.context"]: response["@odata.context"],
        value: response.value.map(
            (attender: CourseAttenderDTO): CourseAttenderDTO => ({
                ["@odata.etag"]: attender["@odata.etag"],
                createdon: attender.createdon,
                exchangerate: attender.exchangerate,
                importsequencenumber: attender.importsequencenumber,
                modifiedon: attender.modifiedon,
                overriddencreatedon: attender.overriddencreatedon,
                statecode: attender.statecode,
                statuscode: attender.statuscode,
                svk_allowshowonweb: attender.svk_allowshowonweb,
                svk_attendingcourse: attender.svk_attendingcourse,
                svk_coruseattenderid: attender.svk_coruseattenderid,
                svk_courseprice: attender.svk_courseprice,
                svk_courseprice_base: attender.svk_courseprice_base,
                svk_invoicetext: attender.svk_invoicetext,
                svk_name: attender.svk_name,
                svk_remark: attender.svk_remark,
                timezoneruleversionnumber: attender.timezoneruleversionnumber,
                utcconversiontimezonecode: attender.utcconversiontimezonecode,
                versionnumber: attender.versionnumber,
                _createdby_value: attender._createdby_value,
                _createdonbehalfby_value: attender._createdonbehalfby_value,
                _modifiedby_value: attender._modifiedby_value,
                _modifiedonbehalfby_value: attender._modifiedonbehalfby_value,
                _ownerid_value: attender._ownerid_value,
                _owningbusinessunit_value: attender._owningbusinessunit_value,
                _owningteam_value: attender._owningteam_value,
                _owninguser_value: attender._owninguser_value,
                _svk_addedby_value: attender._svk_addedby_value,
                _svk_attendercompany_value: attender._svk_attendercompany_value,
                _svk_contact_value: attender._svk_contact_value,
                _svk_coursename_value: attender._svk_coursename_value,
                _svk_invoiceproduct_value: attender._svk_invoiceproduct_value,
                _svk_salesorder_value: attender._svk_salesorder_value,
                _transactioncurrencyid_value: attender._transactioncurrencyid_value,
                svk_customerreference: attender.svk_customerreference
            })
        )
    };
}

export function mapKnowledgeArticles(response: KnowledgeArticlesDTO): KnowledgeArticle[] {
    return response.value.map(
        (article: KnowledgeArticleDTO): KnowledgeArticle => ({
            articleNumber: article.articlepublicnumber,
            articleId: article.knowledgearticleid,
            isFocusarticle: article.svk_focusarticle,
            businessareaId: article._svk_businessarea_value,
            title: article.title,
            majorversionnumber: article.majorversionnumber,
            minorversionnumber: article.minorversionnumber,
            statecode: article.statecode,
            statuscode: article.statuscode,
            description: article.description,
            content: article.content,
            keywords: article.keywords,
            ratingCount: article.rating_count,
            readingTime: article.svk_readingtime,
            createdon: article.createdon,
            publishdate: article.svk_publishdate
        })
    );
}

export function mapKnowledgeArticle(response: KnowledgeArticleDTO): KnowledgeArticle {
    return {
        articleNumber: response.articlepublicnumber,
        articleId: response.knowledgearticleid,
        isFocusarticle: response.svk_focusarticle,
        businessareaId: response._svk_businessarea_value,
        title: response.title,
        majorversionnumber: response.majorversionnumber,
        minorversionnumber: response.minorversionnumber,
        statecode: response.statecode,
        statuscode: response.statuscode,
        description: response.description,
        content: response.content,
        keywords: response.keywords,
        ratingCount: response.rating_count,
        readingTime: response.svk_readingtime,
        createdon: response.createdon,
        publishdate: response.svk_publishdate
    };
}

export function mapFeedbackKnowledgeArticle(response: FeedbackKnowledgeArticle): FeedbackKnowledgeArticle {
    return {
        ["KnowledgeArticleId@odata.bind"]: response["KnowledgeArticleId@odata.bind"],
        title: response.title,
        source: response.source,
        maxrating: response.maxrating
    };
}

export function mapAttachments(response: AttachmentsDTO): Attachment[] {
    return response.value.map(
        (attachment: AttachmentDTO): Attachment => ({
            iconUrl: attachment.msdyn_fileicon_url,
            statecode: attachment.statecode,
            filename: attachment.msdyn_filename,
            filesize: attachment.msdyn_filesize,
            attachmentId: attachment.msdyn_kbattachmentid,
            filetype: attachment.msdyn_filetype,
            articleAttachments: attachment.msdyn_msdyn_kbattachment_knowledgearticle,
            attachment: attachment.attachment
        })
    );
}

export function mapArticleContentAttachment(response: ArticleContentAttachmentDTO): ArticleContentAttachment {
    return {
        value: response.value
    };
}
